/**
 * AdvisorReferralsAdmin Component
 * Super Admin view to manage advisor referrals
 */
import React, { useState, useEffect } from 'react';
import { 
  FaUserTie, FaSpinner, FaPhone, FaEnvelope, FaClock, FaCheckCircle, 
  FaTimesCircle, FaUsers, FaRupeeSign, FaChevronDown, FaComment,
  FaUserCheck, FaUserClock, FaUserPlus
} from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdvisorReferralsAdmin = () => {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [statusNote, setStatusNote] = useState('');

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [referralsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/advisor-referrals`, { headers: headers() }),
        axios.get(`${API}/admin/advisor-referrals/stats`, { headers: headers() })
      ]);
      setReferrals(referralsRes.data);
      setStats(statsRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (referralId, newStatus) => {
    setUpdating(referralId);
    try {
      await axios.put(
        `${API}/admin/advisor-referrals/${referralId}/status`,
        { status: newStatus, notes: statusNote },
        { headers: headers() }
      );
      await fetchData();
      setSelectedReferral(null);
      setStatusNote('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: FaClock, label: 'Pending' },
      contacted: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: FaPhone, label: 'Contacted' },
      registered: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: FaUserPlus, label: 'Registered' },
      onboarded: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: FaCheckCircle, label: 'Onboarded' },
      rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: FaTimesCircle, label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon size={10} />
        <span>{config.label}</span>
      </span>
    );
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: FaClock },
    { value: 'contacted', label: 'Contacted', icon: FaPhone },
    { value: 'registered', label: 'Registered', icon: FaUserPlus },
    { value: 'onboarded', label: 'Onboarded', icon: FaUserCheck },
    { value: 'rejected', label: 'Rejected', icon: FaTimesCircle }
  ];

  if (loading) {
    return (
      <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] p-6">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advisor-referrals-admin">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center">
            <FaUserTie size={24} className="text-[#0A1929]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Advisor Referrals</h2>
            <p className="text-gray-400">Manage advisor referrals from existing advisors</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <div className="text-gray-400 text-sm">Total</div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-yellow-500/30">
            <div className="text-yellow-400 text-sm">Pending</div>
            <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-blue-500/30">
            <div className="text-blue-400 text-sm">Contacted</div>
            <div className="text-2xl font-bold text-blue-400">{stats.contacted}</div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-purple-500/30">
            <div className="text-purple-400 text-sm">Registered</div>
            <div className="text-2xl font-bold text-purple-400">{stats.registered}</div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-green-500/30">
            <div className="text-green-400 text-sm">Onboarded</div>
            <div className="text-2xl font-bold text-green-400">{stats.onboarded}</div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-red-500/30">
            <div className="text-red-400 text-sm">Rejected</div>
            <div className="text-2xl font-bold text-red-400">{stats.rejected}</div>
          </div>
        </div>
      )}

      {/* Referrals Table */}
      <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] overflow-hidden">
        <div className="p-4 border-b border-[#2D5A8F] bg-[#0F172A]/50">
          <h3 className="text-lg font-bold text-white">All Referrals</h3>
        </div>

        {referrals.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <FaUserTie size={48} className="mx-auto mb-4 opacity-50" />
            <p>No advisor referrals yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0F172A]/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Referred Advisor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Referred By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Reward</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2D5A8F]/50">
                {referrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-[#0F172A]/30">
                    <td className="px-4 py-4">
                      <div className="font-medium text-white">{referral.referred_advisor_name}</div>
                      {referral.referred_advisor_profile && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {referral.referred_advisor_profile}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col space-y-1 text-sm">
                        <span className="flex items-center space-x-1 text-gray-300">
                          <FaPhone size={10} />
                          <span>{referral.referred_advisor_contact}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-gray-400">
                          <FaEnvelope size={10} />
                          <span className="truncate max-w-[150px]">{referral.referred_advisor_email}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-[#FFD700]">{referral.referred_by_name}</div>
                      <div className="text-xs text-gray-500">{referral.referred_by_email}</div>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(referral.status)}
                    </td>
                    <td className="px-4 py-4">
                      {referral.status === 'onboarded' ? (
                        <span className="flex items-center space-x-1 text-[#FFD700] font-medium">
                          <FaRupeeSign size={12} />
                          <span>1,500/yr</span>
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-400">
                      {new Date(referral.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-4">
                      <div className="relative">
                        <button
                          onClick={() => setSelectedReferral(selectedReferral === referral.id ? null : referral.id)}
                          disabled={updating === referral.id}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-[#2D5A8F] text-white text-sm rounded-lg hover:bg-[#3D6A9F] transition-all disabled:opacity-50"
                        >
                          {updating === referral.id ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <>
                              <span>Update</span>
                              <FaChevronDown size={10} />
                            </>
                          )}
                        </button>

                        {/* Dropdown Menu */}
                        {selectedReferral === referral.id && (
                          <div className="absolute right-0 mt-2 w-64 bg-[#1A2942] border border-[#2D5A8F] rounded-lg shadow-xl z-10">
                            <div className="p-3 border-b border-[#2D5A8F]">
                              <label className="block text-xs text-gray-400 mb-1">Add Note (optional)</label>
                              <input
                                type="text"
                                value={statusNote}
                                onChange={(e) => setStatusNote(e.target.value)}
                                placeholder="Add a note..."
                                className="w-full px-2 py-1.5 bg-[#0F172A] border border-[#2D5A8F] rounded text-white text-sm placeholder-gray-500"
                              />
                            </div>
                            <div className="p-2">
                              {statusOptions.map((option) => {
                                const Icon = option.icon;
                                return (
                                  <button
                                    key={option.value}
                                    onClick={() => updateStatus(referral.id, option.value)}
                                    disabled={referral.status === option.value}
                                    className={`w-full flex items-center space-x-2 px-3 py-2 text-left text-sm rounded-lg transition-all ${
                                      referral.status === option.value
                                        ? 'bg-[#2D5A8F]/50 text-gray-500 cursor-not-allowed'
                                        : 'hover:bg-[#2D5A8F] text-white'
                                    }`}
                                  >
                                    <Icon size={14} />
                                    <span>{option.label}</span>
                                    {referral.status === option.value && (
                                      <span className="ml-auto text-xs text-gray-500">(current)</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvisorReferralsAdmin;
